/**
 * Every endpoint, in one place, typed against docs/API_SPEC.md.
 *
 * Response shapes mirror the Django serializers. Money is a string wherever the
 * server sends DECIMAL (Kyat) and a number for coins — that asymmetry is
 * deliberate and matches the wire format, so no float ever touches a Kyat value.
 */
import { api } from './client';

/* ── shared shapes ────────────────────────────────────────────────────────── */

export interface WalletDTO {
  coins: number;
  kyat: string;
  kyat_locked?: string;
  kyat_available?: string;
}

export interface EquipmentStats {
  damage?: number; crit_pct?: number; swing_ms?: number; loot_bonus_pct?: number;
  light_px?: number; max_depth_m?: number; rare_bonus_pct?: number; glow_pct?: number;
  torch_fuel?: number; slots?: number; auto_sell?: boolean;
  /**
   * How deep ONE full torch actually carries you. Derived server-side from the run
   * engine's fuel curve, never stored.
   *
   * `max_depth_m` is the CAP — permission, not reach. Fuel burns faster with depth,
   * so from the Torch up every lamp's cap sits beyond a full tank. Both are true and
   * the shop shows both; a client-side copy of that curve would be a second opinion
   * about the server's own physics.
   */
  reach_m?: number;
}

export interface EquipmentDTO {
  level: number;
  name: string;
  stats: EquipmentStats;
  max_level?: number;
  next: {
    level: number; name: string;
    cost_coins: number | null; cost_kyat: string | null;
    stats: EquipmentStats;
  } | null;
}

export interface ActiveCharacterDTO {
  key: string; name: string; level: number;
  bonus: { type: string; value: number };
  special: string; accent: string;
}

export interface MaterialDTO {
  key: string; name: string; color_a: string; color_b: string; hp_multiplier: string;
}

export interface RockDTO {
  spawn_uuid: string;
  key: string; name: string; tier: string;
  material: MaterialDTO | null;
  visual_type: 'trash_rock' | 'treasure_rock';
  glow_pct: number;
  light_px: number;
  required_axe_level: number;
  /** Under-levelled axe. `required_hits` is withheld while true. */
  locked: boolean;
  required_hits: number | null;
  hits_registered: number;
  blessed: boolean;
  slot?: 'path' | 'floor' | 'wall' | 'ceiling';
  slot_u?: number;
  slot_v?: number;
  depth_m?: number;
  spirit: { active: boolean; expires_at: string } | null;
}

export interface LootDTO {
  pool: 'trash' | 'treasure';
  item: {
    key: string; name: string; category: string; rarity: string;
    rarity_color: string; icon_key: string; description: string;
  };
  quantity: number;
  coins: number;
  /** NOT banked. Becomes real only after a verified ad. */
  kyat_pending: string;
  xp: number;
  /** `treasure_claim` or `trash_double` — never any other catalogue key. A rock's ad is
   *  the one place the placement is fixed by the money rules rather than chosen from the
   *  catalogue: trash may only ever double COINS (rule 1, and a DB CHECK constraint behind
   *  it), treasure gates KYAT on a verified callback (rule 2). Typed as the pair so a wrong
   *  placement is a compile error rather than an impression the server refuses. */
  /**
   * The ad on offer for this rock, or `null` when there is none.
   *
   * A treasure rock always carries one: its ad is the only route to the Shwe Kyat already
   * rolled for it, so rationing the offer would ration real money (money rule 2). A trash
   * rock's `trash_double` is offered on a server-side cadence — every third to fifth rock,
   * irregular — because a button on every single break is one players learn to look past.
   *
   * The server decides, and stores the decision on the spawn when the rock breaks. Re-reading
   * the loot cannot re-roll it.
   */
  ad: { required: boolean; placement: 'treasure_claim' | 'trash_double' } | null;
}

export interface RunDTO {
  run_id?: string;
  cave?: string;
  depth_m: number;
  torch_fuel: string;
  torch_fuel_max?: string;
  light_px: number;
  /** null means endless — the last lamp has no ceiling. */
  max_depth_m: number | null;
    /** How deep ONE full torch reaches. Server-computed from the fuel
     *  curve; `max_depth_m` is the CAP, which is a different question. */
    reach_m?: number;
  zone: ZoneDTO;
  best_depth_m?: number;
  active_rock?: RockDTO | null;
}

/**
 * The band the player is standing in, plus the palette of the cave it belongs to.
 *
 * The cave colours ride along on every zone rather than being fetched once, because
 * the alternative is a second request whose answer the corridor needs before its
 * first paint. They are optional so an older server — one that only ever knew about
 * the Abandoned Mine — degrades to the zone tint instead of rendering nothing.
 */
export interface ZoneDTO {
  name: string;
  tint: string;
  /** Cave key, e.g. `glass_reef`. */
  cave?: string;
  cave_name?: string;
  cave_tint?: string;
  /** Torch/ember accent for this cave. */
  glow?: string;
  /** What the far dark fades to. Never `#000` — see `game/engine/lighting.ts`. */
  fog?: string;
}

/**
 * What one descent came to.
 *
 * `/cave/exit` has returned all six of these since the run engine was written; the
 * client declared four and the store discarded every one of them, so a run ended
 * with the player told nothing at all. `kyat` is a string like every DECIMAL on this
 * wire — it is withdrawable, and no float touches it.
 */
export interface RunSummaryDTO {
  depth_m: number;
  best_depth_m: number;
  rocks: number;
  coins: number;
  kyat: string;
  duration_s: number;
}

/** One cave on the map screen, with this player's progress toward it. */
export interface CaveMapDTO {
  key: string;
  name: string;
  subtitle: string;
  description: string;
  tint: string;
  glow: string;
  fog: string;
  coin_multiplier: string;
  is_endless: boolean;
  /** The map is complete. Still not enough on its own — see `level_ok`. */
  unlocked: boolean;
  level_ok: boolean;
  unlock_min_level: number;
  fragments: number;
  fragments_required: number;
  /** Cave key whose walls hide this cave's map. */
  found_in: string | null;
  found_in_name: string | null;
  found_from_depth_m: number;
  zones: { index: number; name: string; tint: string; from_m: number; to_m: number | null }[];
}

/**
 * What a wall search turned up.
 *
 * `type` is the discriminator and the only field always present. A `map_fragment`
 * carries progress toward the cave it belongs to; `opened: true` means that was the
 * last layer and the cave is now enterable.
 */
export interface SearchEventDTO {
  type: 'none' | 'hidden_chest' | 'mystery_door' | 'map_fragment';
  name?: string;
  message: string;
  item?: string;
  coins?: number;
  opened?: boolean;
  cave?: string;
  cave_name?: string;
  fragments?: number;
  required?: number;
}

export interface BootstrapDTO {
  user: {
    uuid: string; display_name: string; level: number; xp: number; xp_to_next: number;
    locale: string; is_guest: boolean; tutorial_step: number; seen_intro: boolean;
    /** Server-decided: guests are never asked for a name or DOB. */
    needs_first_login: boolean;
    ancient_keys: number;
    auth_provider?: string;
    /** See `IdentityDTO`. Optional: an older server omits it, and that means "unknown". */
    identities?: IdentityDTO[];
  };
  wallet: WalletDTO;
  energy: { current: number; max: number; next_regen_in: number };
  equipment: Record<string, EquipmentDTO>;
  active_characters: { explorer: ActiveCharacterDTO | null; companion: ActiveCharacterDTO | null };
  run: RunDTO | { best_depth_m: number; active_rock: null };
  collection: {
    trash_found: number; trash_total: number;
    treasure_found: number; treasure_total: number; score: number;
  };
  flags: {
    ads_enabled: boolean;
    /** Platforms where a rewarded ad can be VERIFIED. See game/ads/availability.ts. */
    ad_reward_platforms: string[];
    ad_reward_web: boolean;
    withdraw_enabled: boolean;
    min_withdraw: string;
  };
  static_version: string;
}

/* ── auth ─────────────────────────────────────────────────────────────────── */

/** One connected social account. A player may hold one per provider — at most three. */
export interface IdentityDTO {
  provider: 'google' | 'apple' | 'telegram';
  email: string | null;
  linked_at: string;
  last_used_at: string | null;
}

export interface UserDTO {
  uuid: string; display_name: string; auth_provider: string;
  is_guest: boolean; level: number; needs_first_login: boolean;
  /**
   * Every connected provider, not just the primary one.
   *
   * `auth_provider` names a single identity and named the WRONG one as soon as a second was
   * connected, so an account screen built on it could not show what is actually linked.
   * Optional because an older server does not send it, and a missing list must read as
   * "unknown", never as "nothing is connected" — the second would offer to connect an
   * account the player already has.
   */
  identities?: IdentityDTO[];
}

export interface AuthDTO {
  access: string; refresh: string; is_new?: boolean;
  user: UserDTO;
}

export interface DeletePreviewDTO {
  coins: number;
  /** Decimal string. Kyat is money — never a float. */
  kyat: string;
  open_withdrawals: number;
  /** A withdrawal in flight blocks deletion outright, rather than forfeiting it. */
  blocked: boolean;
  needs_forfeit_confirmation: boolean;
}

export interface DeleteSummaryDTO {
  deleted_at: string;
  kyat_forfeited: string;
  coins_forfeited: number;
  identities_released: number;
  devices_removed: number;
  sessions_revoked: number;
  ad_impressions_anonymised: number;
  payout_profile_removed: boolean;
}

export const auth = {
  guest: (device_uid: string, platform = 'web') =>
    api.post<AuthDTO>('/auth/guest', { device_uid, platform }, { anonymous: true }),

  social: (provider: 'google' | 'apple' | 'telegram', id_token: string, device_uid: string) =>
    api.post<AuthDTO>('/auth/social', { provider, id_token, device_uid }, { anonymous: true }),

  /** Attach a provider to the signed-in player. Adds; never replaces what is already there. */
  linkGuest: (provider: 'google' | 'apple' | 'telegram', id_token: string, device_uid: string) =>
    api.post<{ linked: boolean; user: UserDTO }>(
      '/auth/link-guest', { provider, id_token, device_uid },
    ),

  /**
   * Disconnect one provider.
   *
   * The server refuses to remove the last one — an account with no way in is reachable by
   * nobody, support included, while still holding a balance.
   */
  unlink: (provider: 'google' | 'apple' | 'telegram') =>
    api.post<{ unlinked: string; user: UserDTO }>('/auth/unlink', { provider }),

  logout: (refresh: string) => api.post<void>('/auth/logout', { refresh }),

  /**
   * Delete this account, permanently.
   *
   * `deletePreview` first: the confirmation has to name what the player is giving up, and a
   * dialog that says "you may lose some currency" is not a confirmation. The server refuses
   * outright while a withdrawal is in flight, and refuses without `confirm_forfeit` whenever
   * there is a Shwe Kyat balance — Kyat converts to real money, so voiding it silently would
   * be taking something at the moment the player is least able to notice.
   *
   * `confirm: 'DELETE'` is a literal the server checks. An empty body cannot destroy an
   * account by accident.
   */
  deletePreview: () => api.get<DeletePreviewDTO>('/auth/account/delete'),

  deleteAccount: (confirmForfeit: boolean) =>
    api.post<DeleteSummaryDTO>(
      '/auth/account/delete', { confirm: 'DELETE', confirm_forfeit: confirmForfeit },
    ),

  /** One-time. Name and date of birth are not editable afterwards. */
  firstLogin: (v: {
    display_name: string; date_of_birth: string; country_code: string; locale: string;
  }) => api.post<{ user: AuthDTO['user'] }>('/auth/first-login', v),

  tutorial: (step: number, seen_intro?: boolean) =>
    api.post<{ tutorial_step: number }>('/auth/tutorial', { step, seen_intro }),

  payoutProfile: {
    /* Shape mirrors PayoutProfileView.get. The account number is only ever
       returned masked — the plaintext never leaves the server. */
    get: () => api.get<{
      exists: boolean;
      eligible?: boolean;
      is_locked?: boolean;
      country_code?: string;
      method?: string;
      bank_name?: string;
      account_name?: string;
      account_masked?: string;
      phone?: string;
    }>('/auth/payout-profile'),
    create: (v: {
      country_code: string; method: string; bank_name?: string;
      account_name: string; account_number: string; phone: string;
    }) => api.post<{ locked: boolean; account_masked: string }>('/auth/payout-profile', v),
  },
};

/* ── boot ─────────────────────────────────────────────────────────────────── */

export const boot = {
  bootstrap: () => api.get<BootstrapDTO>('/bootstrap'),
  /** ETag-cached server-side; safe to call anonymously. */
  gamedata: () => api.get<Record<string, unknown>>('/gamedata', { anonymous: true }),
};

/* ── the cave loop ────────────────────────────────────────────────────────── */

export interface StepDTO extends RunDTO {
  event: { type: string; key?: string; name?: string; message?: string };
  obstacle: { type: 'rock' | 'none'; rock?: RockDTO };
  /** Every rock the lamp reaches — floor, wall and ceiling — with server-chosen
   *  placement in `slot`/`slot_u`/`slot_v` and its absolute `depth_m`. Nearest first,
   *  capped at 20 by `FIELD_VISIBLE_MAX`. `obstacle.rock` is the *featured* one, which
   *  also appears here with `slot: 'path'`. Optional only so existing fixtures need
   *  not construct it; the server always sends it. */
  spawns?: RockDTO[];
  energy: { current: number; max: number; next_regen_in: number };
}

export interface HitDTO {
  hits_registered: number;
  required_hits: number;
  crit: boolean;
  heavy_swing?: boolean;
  crack_stage: number;
  broken: boolean;
  loot?: LootDTO;
}

export interface ClaimDTO {
  granted: {
    coins: number; kyat: string; xp: number;
    item: { key: string; quantity: number };
    extra_item: string | null;
  };
  /** What the player walked away from by skipping the ad. Shown struck through. */
  forfeited_kyat: string;
  multiplier_applied: string;
  auto_collected: boolean;
  ad_verified: boolean;
  wallet: WalletDTO;
  profile: { level: number; xp: number; xp_to_next: number; levels_gained: number[]; ancient_keys: number };
}

export const cave = {
  enter: (cave_key = 'abandoned_mine') =>
    api.post<RunDTO & { spawns?: RockDTO[] }>('/cave/enter', { cave: cave_key }),

  step: () => api.post<StepDTO>('/cave/step', {}),

  /** Walk back up the tunnel. Same payload as `step`, and `obstacle` is always
   *  `none` — walking back re-reveals rock, it never rolls any. */
  stepBack: () => api.post<StepDTO>('/cave/back', {}),

  /** Every cave and how much of its map this player has. Locked ones included. */
  maps: () => api.get<{ caves: CaveMapDTO[]; current: string | null }>('/cave/maps'),

  search: (side: 'left' | 'right') => api.post<RunDTO & { event: SearchEventDTO }>(
    '/cave/search', { side }),

  hit: (spawn_uuid: string) =>
    api.post<HitDTO>('/cave/hit', { spawn_uuid, client_tap_at: Date.now() }),

  /** Idempotent: a retried claim must not pay twice. */
  claim: (spawn_uuid: string, impression_uuid?: string) =>
    api.mutate<ClaimDTO>('/cave/claim', { spawn_uuid, impression_uuid: impression_uuid ?? null }),

  exit: () => api.post<RunSummaryDTO>('/cave/exit', {}),

  state: () => api.get<{ active: boolean; spawns?: RockDTO[] } & Partial<RunDTO>>('/cave/state'),
};

/* ── equipment + characters ───────────────────────────────────────────────── */

export const equipment = {
  list: () => api.get<Record<string, EquipmentDTO>>('/equipment'),

  upgrade: (key: 'axe' | 'lamp' | 'backpack', pay_with: 'coins' | 'kyat' = 'coins') =>
    api.mutate<{ level: number; name: string; stats: EquipmentStats; wallet: WalletDTO }>(
      `/equipment/${key}/upgrade`, { pay_with }),
};

export interface CharacterRowDTO {
  key: string; name: string; title: string; role: string; slot: string;
  stars: string; accent: string; description: string;
  special: { key: string; description: string };
  bonus: { type: string; base: string; effective: string };
  unlocked: boolean; active: boolean; level: number; max_level: number;
  unlock: { coins: number | null; kyat: string | null; achievement: string | null };
  next_level: { level: number; cost_coins: number | null; multiplier: string } | null;
}

export const characters = {
  list: () => api.get<{ roster: CharacterRowDTO[] }>('/equipment/characters'),
  unlock: (key: string, pay_with: 'coins' | 'kyat' = 'coins') =>
    api.mutate<{ unlocked: boolean; wallet: WalletDTO }>(
      `/equipment/characters/${key}/unlock`, { pay_with }),
  activate: (key: string) =>
    api.post<{ active: string; slot: string }>(`/equipment/characters/${key}/activate`, {}),
  upgrade: (key: string) =>
    api.mutate<{ level: number; effective_bonus: string; wallet: WalletDTO }>(
      `/equipment/characters/${key}/upgrade`, {}),
};

/* ── inventory ────────────────────────────────────────────────────────────── */

export interface InventoryRowDTO {
  key: string; name: string; pool: 'trash' | 'treasure'; category: string;
  rarity: string; rarity_color: string; icon_key: string; description: string;
  coin_value: number; kyat_value: string; sell_value: number; xp_value: number;
  quantity: number; total_found: number; is_new: boolean; discovered: boolean;
}

export const inventory = {
  list: (pool?: 'trash' | 'treasure') =>
    api.get<{ items: InventoryRowDTO[]; used_slots: number; max_slots: number }>(
      `/inventory${pool ? `?pool=${pool}` : ''}`),

  collection: () => api.get<{
    items: InventoryRowDTO[]; trash_found: number; trash_total: number;
    treasure_found: number; treasure_total: number; completion_pct: number;
    bonus_tiers: { tier: number; reward_coins: number; claimable: boolean }[];
  }>('/collection'),

  sell: (item_key: string, quantity = 1) =>
    api.mutate<{ sold: number; coins_gained: number; wallet: WalletDTO }>(
      '/inventory/sell', { item_key, quantity }),

  sellAllTrash: () =>
    api.mutate<{ sold: number; coins_gained: number; wallet: WalletDTO }>(
      '/inventory/sell-all-trash', {}),

  markSeen: (item_keys: string[]) => api.post<{ cleared: number }>('/inventory/seen', { item_keys }),
};

/* ── ads ──────────────────────────────────────────────────────────────────
   The three-leg handshake. The client never grants itself a reward: it asks for
   an impression, shows the ad, then polls until the server has seen AdMob's
   signed SSV callback. See docs/ARCHITECTURE.md §3. */

export interface AdRequestDTO {
  impression_uuid: string;
  nonce: string;
  ad_unit_id: string;
  ssv_custom_data: string;
  expires_in: number;
}

export const ads = {
  hub: () => api.get<{
    enabled: boolean;
    offers: {
      key: string; name: string; description: string; reward_type: string;
      reward_amount: string | null; available: boolean; reason: string;
      remaining_today: number; cooldown_remaining: number;
    }[];
  }>('/ads/hub'),

  request: (placement: string, ref?: { type: string; id: string }) =>
    api.post<AdRequestDTO>('/ads/request', { placement, ref: ref ?? null }),

  /** Resolves once the SSV callback has landed. Throws AD_NOT_VERIFIED to retry. */
  complete: (impression_uuid: string) =>
    api.post<{
      verified: boolean;
      reward: { currency: string; amount: string };
      wallet: WalletDTO;
    }>('/ads/complete', { impression_uuid }),

  history: (source?: string) => api.get<{
    results: { source: string; title: string; currency: string; amount: string; created_at: string }[];
  }>(`/ads/history${source ? `?source=${source}` : ''}`),
};

/* ── wallet ───────────────────────────────────────────────────────────────── */

export const wallet = {
  get: () => api.get<WalletDTO & { lifetime_kyat_earned: string }>('/wallet'),

  transactions: (currency?: 'coin' | 'kyat') => api.get<{
    results: {
      uuid: string; currency: string; direction: string; amount: string;
      balance_after: string; reason: string; note: string; created_at: string;
    }[];
  }>(`/wallet/transactions${currency ? `?currency=${currency}` : ''}`),

  withdrawals: () => api.get<{
    results: {
      uuid: string; amount_kyat: string; status: string; status_reason: string;
      account_masked: string; created_at: string; paid_at: string | null;
    }[];
    min_amount: string; max_amount: string; max_per_week: number;
  }>('/wallet/withdrawals'),

  requestWithdrawal: (amount_kyat: string) =>
    api.mutate<{ uuid: string; status: string; wallet: WalletDTO }>(
      '/wallet/withdrawals', { amount_kyat }),

  cancelWithdrawal: (uuid: string) =>
    api.post<{ status: string }>(`/wallet/withdrawals/${uuid}/cancel`, {}),
};

/* ── progression ──────────────────────────────────────────────────────────── */

export const progress = {
  missions: () => api.get<{
    results: {
      key: string; name: string; description: string; scope: string;
      progress: number; target: number; reward_coins: number; reward_kyat: string;
      completed: boolean; claimable: boolean;
    }[];
  }>('/progress/missions'),
  claimMission: (key: string) => api.mutate<unknown>(`/progress/missions/${key}/claim`, {}),

  achievements: () => api.get<{
    results: {
      key: string; name: string; description: string; metric: string; threshold: number;
      progress: number; unlocked: boolean; claimable: boolean;
      reward_coins: number; reward_kyat: string;
    }[];
  }>('/progress/achievements'),
  claimAchievement: (key: string) => api.mutate<unknown>(`/progress/achievements/${key}/claim`, {}),

  daily: () => api.get<{ streak: number; claimed_today: boolean; next_day_index: number }>(
    '/progress/daily'),
  claimDaily: () => api.mutate<unknown>('/progress/daily', {}),

  statistics: () => api.get<Record<string, unknown>>('/progress/statistics'),

  leaderboard: (board = 'depth', period?: string) => api.get<{
    board: string; period: string;
    results: { rank: number; display_name: string; score: number }[];
    me: { rank: number | null; score: number } | null;
  }>(`/progress/leaderboard?board=${board}${period ? `&period=${period}` : ''}`),
};

/* ── meta ─────────────────────────────────────────────────────────────────── */

export const meta = {
  health: () => api.get<{ status: string }>('/meta/health', { anonymous: true }),
  announcements: (locale?: string) => api.get<{ results: unknown[] }>(
    `/meta/announcements${locale ? `?locale=${locale}` : ''}`, { anonymous: true }),
  report: (subject: string, body: string, category = 'other') =>
    api.post<{ id: number }>('/meta/reports', { subject, body, category }),
};
